import React, { useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { motion, AnimatePresence } from 'framer-motion';
import ChatView from './ChatView';
import WelcomeScreenWithAgents from './WelcomeScreenWithAgents';
import ThankYouScreen from './ThankYouScreen';
import UserInput from './UserInput';
import Footer from './Footer';
import { useParentWindowSize } from '../hooks/useParentWindowSize';
import { contrastStyleColor } from '../utils/colors';
import { MOCK_WINDOW_CONFIG } from '../utils/constants';
import { fetchToken, parseJWT } from '../utils/session_token';

import '../styles/Chat.scss';
import '../styles/TriggerButton.scss';
import ChatBubble from './ChatBubble';

const VIEW_STATES = {
  welcomeNoAgents: 'welcomeNoAgents',
  welcomeWithAgents: 'welcomeWithAgents',
  chatView: 'chatView',
  thankYou: 'thankYou'
};

const EVENT_TYPES = {
  message: 'message',
  requestAvailableAgents: 'requestAvailableAgents'
};

const MESSAGE_HISTORY_LIMIT = 1000;
const BASE_URL = `${process.env.REACT_APP_LIVECHAT_WS}/connection/websocket`;
const DEFAULT_TEXT_COLOR = 'text-white';

const ChatContainer = ({ sandbox }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewState, setViewState] = useState(VIEW_STATES.chatView);
  const [availableAgents, setAvailableAgents] = useState([false]);
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [userMessageType, setUserMessageType] = useState('text');
  const [agentMessageType, setAgentMessageType] = useState('text');
  const [userMessageText, setUserMessageText] = useState('');
  const [agentMessageText, setAgentMessageText] = useState('');

  // config params
  const [chatButtonImage, setChatButtonImage] = useState(null);
  const [initialMessage, setInitialMessage] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(null);
  const [showAvailableAgents, setShowAvailableAgents] = useState(true);
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);

  const [token, setToken] = useState(null);
  const [livechatUuid, setLivechatUuid] = useState(null);
  const [channelName, setChannelName] = useState(null);
  const [contactName, setContactName] = useState(null);
  const parentWindowSize = useParentWindowSize();
  const centrifuge = useRef(null);

  const mockMessages = [
    {
      senderType: 'agent',
      type: 'text',
      content: "Hello! 👋 I'm here to help you. How can I assist you today?",
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      senderType: 'user',
      type: 'text',
      content: 'Hello! I would like to know more about the livechat.',
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      senderType: 'agent',
      type: 'text',
      content:
        "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when a",
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      senderType: 'agent',
      type: 'document',
      content:
        "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when a",
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      senderType: 'agent',
      type: 'image',
      content:
        "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's",
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  ];

  const [messages, setMessages] = useState(sandbox ? mockMessages : []);

  const toggleChat = async () => {
    setIsOpen(!isOpen);

    if (!isOpen && centrifuge.current) {
      await getAvailableAgents();
    }
  };

  const getAvailableAgents = async () => {
    const requestAvailableAgents = {
      metadata: {
        callbell_livechat_uuid: livechatUuid,
        event_type: EVENT_TYPES.requestAvailableAgents
      },
      senderType: 'user',
      sender: { email: null, name: null, token: token },
      timestamp: new Date().toISOString()
    };

    centrifuge.current.publish(channelName, {
      params: requestAvailableAgents
    });
  };

  const userStartedConversation = async () => {
    sendMessage('Start conversation 🚀', 'user', 'text', null, contactName);
  };

  const shouldShowUserInput = [VIEW_STATES.chatView].includes(viewState);

  const formattedNewMessageForUI = (
    senderType,
    type,
    content,
    timestamp = 'today',
    options = [],
    attachment = null
  ) => {
    const date = timestamp === 'today' ? new Date() : new Date(timestamp);

    return {
      senderType,
      type,
      content,
      time: date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      options,
      attachment
    };
  };

  // inbound
  const sendMessage = (
    content,
    senderType,
    type = 'text',
    attachment = null,
    contactName = null
  ) => {
    const timestamp = new Date().toISOString();
    const newMessagePayload = {
      metadata: {
        callbell_livechat_uuid: livechatUuid,
        event_type: EVENT_TYPES.message
      },
      senderType,
      sender: { email: null, name: contactName, token: token },
      message: {
        text: content,
        message_type: type,
        attachment: attachment
      },
      agents: {
        assigned_agent: assignedAgent
      },
      timestamp
    };

    const newMessage = formattedNewMessageForUI(
      senderType,
      type,
      content,
      timestamp,
      null,
      attachment
    );

    centrifuge.current.publish(channelName, {
      params: newMessagePayload
    });

    setMessages((messages) => [...messages, newMessage]);
  };

  const onUserMessageSend = () => {
    if (userMessageText.trim() !== '') {
      sendMessage(userMessageText, 'user', userMessageType);
      setUserMessageText('');
    }
  };

  const onAgentMessageSend = () => {
    if (agentMessageText.trim() !== '') {
      sendMessage(agentMessageText, 'agent', agentMessageType);
      setAgentMessageText('');
      setIsAgentTyping(false);
    }
  };

  const refreshSession = async () => {
    const newToken = await fetchToken(livechatUuid);

    return newToken;
  };

  const initializeSession = async () => {
    let token = await refreshSession();

    const parsedToken = parseJWT(token);
    const isTokenExpired = new Date(parsedToken.exp * 1000) < new Date();

    if (isTokenExpired) {
      token = await refreshSession();
    }

    setToken(token);
    setChannelName(parseJWT(token).room.replaceAll(':', '_'));
  };

  useEffect(() => {
    const iframeContainer = window.parent?.document?.getElementById('callbell-iframe');

    if (iframeContainer) {
      iframeContainer.style.width = '410px';
      iframeContainer.style.setProperty('bottom', '0px');
      if (customization?.defaultPosition) {
        iframeContainer.style.setProperty('right', '20px');
        iframeContainer.style.setProperty('left', 'initial');
      } else {
        iframeContainer.style.setProperty('left', '20px');
        iframeContainer.style.setProperty('right', 'initial');
      }
      iframeContainer.style.setProperty('top', 'initial');
      iframeContainer.style.setProperty('position', 'fixed');
      iframeContainer.style.setProperty('backface-visibility', 'hidden');

      if (isOpen) {
        const chatContainer = window.document?.getElementById('callbell-chat-container');

        if (parentWindowSize.width < 720) {
          iframeContainer.style.setProperty('width', '100%');
          iframeContainer.style.setProperty('height', '100%');
          iframeContainer.style.setProperty('right', '0px');
          iframeContainer.style.setProperty('left', '0px');
          iframeContainer.style.setProperty('top', '0px');
          iframeContainer.style.setProperty('bottom', '0px');
          iframeContainer.style.setProperty('position', 'absolute');

          chatContainer.style.setProperty('width', '100%');
          chatContainer.style.setProperty('height', '85%');
          chatContainer.style.setProperty('right', '0px');
          chatContainer.style.setProperty('left', '0px');
          chatContainer.style.setProperty('top', '0px');
          chatContainer.style.setProperty('bottom', '0px');
          chatContainer.style.setProperty('inset', '0px');

          return;
        }

        iframeContainer.style.setProperty('bottom', '0px');
        iframeContainer.style.setProperty('position', 'fixed');
        iframeContainer.style.height = '680px';
        return;
      }

      iframeContainer.style.height = '100px';
    }
  }, [isOpen, parentWindowSize]);

  useEffect(() => {
    process.env.NODE_ENV === 'production'
      ? setLivechatUuid(window.parent?.window?.callbellSettings?.uuid)
      : setLivechatUuid(process.env.REACT_APP_LOCAL_LIVECHAT_UUID);

    if (livechatUuid) {
      initializeSession();
    }
  }, [livechatUuid, setToken, setChannelName]);

  useEffect(() => {
    // info from backend
    const iframeContainer = window.parent?.document?.getElementById('callbell-iframe');

    if (iframeContainer) {
      iframeContainer.style.width = '400px';
      iframeContainer.style.height = '100px';
    }

    if (window.config?.mode === 'previewMessages') {
      setViewState(VIEW_STATES.chatView);
    }

    const config =
      process.env.NODE_ENV === 'production' ? window.config.data : MOCK_WINDOW_CONFIG;
    if (config) {
      setChatButtonImage(config.buttonImage);
      setInitialMessage(config.initialMessage);
      setCustomization(config.customization);
      setPrimaryColor(config.primaryColor);
      setTextColor(contrastStyleColor(config.primaryColor));
      setShowAvailableAgents(config.showAvailableAgents);
    }
  }, []);

  // outbound
  useEffect(() => {
    const init = async () => {
      if (channelName) {
        centrifuge.current = new Centrifuge(BASE_URL, {
          token: token,
          getToken: (_token) => initializeSession()
        });

        const sub = centrifuge.current.newSubscription(channelName);

        sub.subscribe();

        centrifuge.current.connect();

        sub.on('publication', (ctx) => {
          const params = ctx.data?.params;

          if (params.metadata.event_type === EVENT_TYPES.message) {
            const newMessage = formattedNewMessageForUI(
              params.senderType,
              params.message.message_type,
              params.message?.text,
              params.timestamp,
              params.message?.options,
              params.message?.url
            );

            onNewMessage(newMessage);
            setAssignedAgent(params?.agents?.assigned_agent);
          }

          if (params.metadata.event_type === EVENT_TYPES.requestAvailableAgents) {
            const agentsParams = params?.agents?.available_agents;

            if (agentsParams) {
              setAvailableAgents(agentsParams);
            }
          }
        });

        const history = await centrifuge.current.history(channelName, {
          limit: MESSAGE_HISTORY_LIMIT
        });
        const publications = history?.publications;
        const messageHistory = publications?.filter(
          (pub) => pub?.data?.params?.metadata?.event_type === EVENT_TYPES.message
        );

        if (messageHistory.length === 0) {
          setViewState(VIEW_STATES.welcomeWithAgents);
        } else {
          setViewState(VIEW_STATES.chatView);
        }

        if (messageHistory?.length > 0) {
          let previousMessages = [];

          messageHistory?.map((pub) => {
            const messageParams = pub?.data?.params;
            const message = messageParams?.message;

            if (message) {
              const newMessage = formattedNewMessageForUI(
                messageParams.senderType,
                message.message_type,
                message?.text,
                'today',
                message?.options,
                message?.url
              );

              previousMessages.push(newMessage);
            }
          });

          setAssignedAgent(
            messageHistory[messageHistory.length - 1]?.data?.params?.agents
              ?.assigned_agent
          );

          setMessages(previousMessages);
        }
      }
    };

    init();

    return () => {
      if (centrifuge.current) {
        console.log('disconnecting centrifuge');
        centrifuge.current.disconnect();
      }
    };
  }, [channelName, token]);

  const onNewMessage = (message) => {
    if (message.senderType === 'user') {
      return;
    }

    setMessages((messages) => [...messages, message]);
  };

  const handleStartConversation = () => {
    userStartedConversation();

    if (availableAgents.length > 0) {
      setViewState(VIEW_STATES.chatView);
    }
  };

  return (
    <>
      {/* Testing Section */}
      {sandbox && (
        <div className="testing">
          <button
            onClick={() => setViewState(VIEW_STATES.welcomeNoAgents)}
            className="bg-callbell-green text-white px-4 py-2 rounded-xl m-2"
          >
            Welcome Screen (No Agents)
          </button>
          <button
            onClick={() => setViewState(VIEW_STATES.welcomeWithAgents)}
            className="bg-callbell-green text-white px-4 py-2 rounded-xl m-2"
          >
            Welcome Screen With Agents
          </button>
          <button
            onClick={() => setViewState(VIEW_STATES.chatView)}
            className="bg-callbell-green text-white px-4 py-2 rounded-xl m-2"
          >
            Chat View
          </button>
          <button
            onClick={() => setViewState(VIEW_STATES.thankYou)}
            className="bg-callbell-green text-white px-4 py-2 rounded-xl m-2"
          >
            Thank You Screen
          </button>
          <hr></hr>
          <span className="text-sm font-bold mt-5">Test message:</span>
          <div className="flex flex-col">
            <select
              value={userMessageType}
              onChange={(e) => setUserMessageType(e.target.value)}
              className="select m-2 text-sm border rounded-xl px-4 py-2"
            >
              <option value="text">Text</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
            </select>
            <input
              type="text"
              value={userMessageText}
              onChange={(e) => setUserMessageText(e.target.value)}
              placeholder="User test message"
              className="input m-2 text-sm border rounded-xl px-4 py-2"
            />
            <button
              onClick={onUserMessageSend}
              className="btn bg-callbell-green text-white m-2 px-4 py-2 rounded-xl"
            >
              Send as User
            </button>
          </div>
          <div className="flex flex-col">
            <select
              value={agentMessageType}
              onChange={(e) => setAgentMessageType(e.target.value)}
              className="select m-2 text-sm border rounded-xl px-4 py-2"
            >
              <option value="text">Text</option>
              <option value="image">Image</option>
            </select>
            <input
              type="text"
              value={agentMessageText}
              onChange={(e) => {
                setAgentMessageText(e.target.value);
                setIsAgentTyping(e.target.value !== '');
              }}
              placeholder="Type an agent message"
              className="input m-2 text-sm border rounded-xl px-4 py-2"
            />
            <button
              onClick={onAgentMessageSend}
              className="btn bg-callbell-green text-white m-2 px-4 py-2 rounded-xl"
            >
              Send as Agent
            </button>
          </div>
        </div>
      )}
      {/* Finish Testing Section */}

      <section className="relative">
        <motion.aside
          className={`cursor-pointer z-[9999] bottom-5 fixed gap-4 flex items-center ${
            customization?.defaultPosition ? 'right-5' : 'left-5'
          }`}
          onClick={toggleChat}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {customization?.defaultPosition ? (
            <>
              {!isOpen && (
                <motion.div
                  className="chat-bubble font-bold"
                  whileHover={{ scale: 1.1 }}
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {initialMessage}
                </motion.div>
              )}
              <ChatBubble
                chatButtonImage={chatButtonImage}
                primaryColor={primaryColor}
                textColor={textColor}
              />
            </>
          ) : (
            <>
              <ChatBubble
                chatButtonImage={chatButtonImage}
                primaryColor={primaryColor}
                textColor={textColor}
              />
              {!isOpen && (
                <motion.div
                  className="chat-bubble-left font-bold"
                  whileHover={{ scale: 1.1 }}
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {initialMessage}
                </motion.div>
              )}
            </>
          )}
        </motion.aside>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={`chat-container bottom-24 rounded-3xl fixed overflow-hidden justify-between pt-0 w-96 h-96 min-w-80 top-0 ${
                customization?.defaultPosition ? 'right-5' : 'left-5'
              }`}
              variants={{
                open: {
                  opacity: 1,
                  transition: { duration: 0.2 }
                },
                closed: {
                  opacity: 0,
                  transition: { duration: 0.2 }
                }
              }}
              initial="closed"
              animate="open"
              exit="closed"
              id="callbell-chat-container"
            >
              <div className="flex-grow overflow-hidden">
                {viewState === VIEW_STATES.welcomeWithAgents && (
                  <WelcomeScreenWithAgents
                    availableAgents={availableAgents}
                    onStartConversation={handleStartConversation}
                    onContactNameChange={(name) => setContactName(name)}
                    showStartConversation={!!contactName}
                    messages={messages}
                    showAvailableAgents={showAvailableAgents}
                    customization={customization}
                    selectedColor={primaryColor}
                    textColor={textColor}
                  />
                )}
                {viewState === VIEW_STATES.chatView && (
                  <ChatView
                    assignedAgent={assignedAgent}
                    onMessageSend={(content, type) => sendMessage(content, 'user', type)}
                    messages={messages}
                    isAgentTyping={isAgentTyping}
                    buttonReplyAnswer={(buttonName) => sendMessage(buttonName, 'user')}
                    selectedColor={primaryColor}
                    textColor={textColor}
                  />
                )}
                {viewState === VIEW_STATES.thankYou && <ThankYouScreen />}
              </div>

              <div className="mt-auto">
                {shouldShowUserInput && (
                  <div className="mt-auto">
                    <UserInput
                      livechatUuid={livechatUuid}
                      channelName={channelName}
                      onMessageSend={(content, type, attachment) =>
                        sendMessage(content, 'user', type, attachment)
                      }
                      selectedColor={primaryColor}
                      textColor={textColor}
                      customization={customization}
                    />
                  </div>
                )}
              </div>
              <Footer />
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </>
  );
};

export default ChatContainer;
